import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import HeroBackdrop from './HeroBackdrop';
import StartEditingButton from './StartEditingButton';
import TrackedVideo from './TrackedVideo';
import { trackEvent } from '../analytics';
import { PRICING_TIERS } from '../pricing/plans';
import prefersReducedMotion from '../lib/prefersReducedMotion';
import forceMuted from '../lib/forceMuted';
import './LandingPage.css';

// The homepage's MotionPace treatment (#545) is pulled in behind the RAW env
// var, exactly as App.js pulls in the /motionpace page, and for the same reason:
// CRA's DefinePlugin substitutes `process.env.REACT_APP_*` at parse time, so
// webpack folds this to `false` for production and never adds the module — or
// its stylesheet, or the MotionPace hero clip — to the graph.
//
// It matters more here than it does for /motionpace. LandingPage.js ships in
// every build, so writing the MotionPace markup inline would leave the copy for
// an unlaunched feature sitting in production's main.js. Reading the shared
// `MOTIONPACE_MARKETING_ENABLED` constant instead of this literal would do the
// same, because an imported binding is opaque to DefinePlugin.
//
// Asserted by src/__tests__/motionPaceGating.test.js.
const motionPaceHome =
  process.env.REACT_APP_MOTIONPACE_MARKETING === 'true'
    // eslint-disable-next-line global-require
    ? require('./landing/motionPaceHome')
    : null;

const SECTION_IDS = [
  'hero',
  ...(motionPaceHome ? [motionPaceHome.SECTION_ID] : []),
  'ingest',
  'funnel',
  'features',
  'brand',
  'tour',
  'pricing',
  'faq',
  'final_cta',
];

// Derived from PRICING_TIERS so the numbers can't drift from /pricing, but
// deliberately not tier.features: that copy names 4K, and advertising export
// resolution to a logged-out visitor is a separate decision (see the
// false-capability guards in LandingPage.test.js).
const planBullets = (tier) => [
  `${tier.storageGb} GB pooled storage`,
  tier.monthlyCredits
    ? `${tier.monthlyCredits} render credits every month`
    : `${tier.signupCredits} starter credits`,
  tier.watermarkFree ? 'Watermark-free exports' : 'Renders carry a Tweedia watermark',
  tier.planId
    ? 'Raw photos kept while you are subscribed'
    : "Raw photos cleared 30 days after a project's last activity",
];

const FAQS = [
  {
    q: 'Do I need a special camera?',
    a: `No. If it produces photos or video with timestamps, Tweedia can use it — GoPro, phone,
        DSLR, jobsite cam, or a Raspberry Pi on a fence post. There is nothing to install and no
        camera subscription to buy.`,
  },
  {
    q: 'What if I disagree with the automatic edit?',
    a: `Then change it. Every step is a switch you can turn off, and the ones that shape the cut —
        how much movement counts, how short a clip can be, how hard to smooth the flicker — are
        yours to set, with the result on screen before you render.`,
  },
  {
    q: 'What does a render cost?',
    a: `Credits, priced by how much footage you feed in rather than which features you switch on.
        Tuning and previewing are always free.`,
  },
  {
    q: 'What happens to my raw photos?',
    a: `They stay in cloud storage while you are subscribed, so the shoot is not stranded on one SD
        card. On the free plan they are cleared 30 days after a project's last activity.`,
  },
];

function LandingPage({ user, signIn }) {
  const sectionRefs = useRef({});
  const signedIn = !!user;

  // The hero proof clips are the page's strongest evidence, and .hero-proof-cell
  // is solid black behind them — so a clip that never starts is a black
  // rectangle under a caption claiming 0:29 -> 0:12. Two ways that happens:
  // the visitor asked for reduced motion (WCAG 2.2.2 also wants a pause control
  // on 29s of looping motion), or the browser simply refused autoplay (iOS Low
  // Power Mode, Chrome Data Saver, enterprise policy). Posters cover the still
  // comparison in both cases; controls give the visitor a way back to playback.
  const [reduceMotion] = useState(prefersReducedMotion);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const showProofControls = reduceMotion || autoplayBlocked;

  const proofVideoRef = useCallback(
    (el) => {
      if (!el) return;
      // BEFORE the reduced-motion return, and unconditionally: `/` is
      // prerendered, and React writes `muted` as a property only, so the JSX
      // prop above reaches neither the snapshot nor the hydrated node. Without
      // this the served HTML is `<video autoplay playsinline>` UNMUTED and
      // Chrome refuses to start it for any first-time visitor — the same #582
      // failure the MotionPace hero had. See lib/forceMuted.
      forceMuted(el);
      if (reduceMotion) {
        // Imperative, not left to the JSX above, and it is `forceMuted` that
        // makes it necessary. The prerender snapshot is captured with motion
        // allowed, so the shipped HTML always reads `<video autoplay loop>`
        // with no `controls`; React 18 hydration warns about mismatched
        // attributes without repairing them, and `reduceMotion` is initial
        // state that never re-renders. Until now the MISSING `muted` was the
        // only thing stopping that parse-time autoplay — a reduced-motion
        // visitor got stills because Chrome refused an unmuted start, not
        // because we asked it to stop. Now that the snapshot carries `muted`,
        // the parser really does start these clips before the bundle lands, so
        // the pause has to be real. Same shape as MotionPacePage.js (#582).
        el.autoplay = false;
        el.loop = false;
        el.controls = true;
        el.pause?.(); // the prerendered HTML may already have started it
        return;
      }
      // autoPlay already asked; this surfaces the refusal so we can offer controls.
      el.play?.()?.catch?.(() => setAutoplayBlocked(true));
    },
    [reduceMotion]
  );

  const handleCtaClick = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: signedIn });
  };

  const handleSignInCta = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: false });
    signIn();
  };

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const fired = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        // Tall sections (e.g. pricing on mobile) may never reach 40% of their
        // own height in-viewport, so also count filling half the viewport.
        const rootHeight = entry.rootBounds ? entry.rootBounds.height : 0;
        const visibleEnough = entry.intersectionRatio >= 0.4 ||
          (rootHeight > 0 && entry.intersectionRect && entry.intersectionRect.height >= rootHeight * 0.5);
        if (entry.isIntersecting && visibleEnough) {
          const id = entry.target.getAttribute('data-section-id');
          if (id && !fired.has(id)) {
            fired.add(id);
            trackEvent('section_view', { section_id: id });
            observer.unobserve(entry.target);
          }
        }
      });
    }, { threshold: [0, 0.1, 0.2, 0.3, 0.4] });
    SECTION_IDS.forEach((id) => {
      const el = sectionRefs.current[id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const setSectionRef = (id) => (el) => {
    sectionRefs.current[id] = el;
  };

  const primaryCta = user ? (
    <Link
      to="/studio"
      className="try-now-link"
      data-testid="hero-cta-primary"
      onClick={handleCtaClick('hero_primary')}
    >
      <StartEditingButton />
    </Link>
  ) : (
    <button
      type="button"
      className="cta-button cta-button-primary"
      onClick={handleSignInCta('hero_primary')}
      data-testid="hero-cta-primary"
    >
      Sign Up Free
    </button>
  );

  return (
    <div className="landing-page">
      <Header user={user} signIn={signIn} />
      <main className="landing-content">
        <section className="hero-section" ref={setSectionRef('hero')} data-section-id="hero">
          <HeroBackdrop />
          <div className="hero-content">
            {/* The empty span goes display:block on desktop so the headline
                breaks between its two sentences instead of mid-clause. Kept as
                a span (not <br>) so the h1's text stays one readable string. */}
            <h1 className="hero-title">
              Months of frames in.{' '}
              <span className="hero-title-break" />
              A two-minute video out.
            </h1>
            <p className="subtitle">
              Point a camera at something slow — a build, a renovation, a season — and Tweedia
              turns what it captured into a short video worth watching. It cuts the hours where
              nothing happens, steadies the flicker, and keeps your footage filed by capture day.
              All in the browser.
            </p>
            <div className="hero-cta-row">
              {primaryCta}
              <a
                href="#tour"
                className="cta-button cta-button-secondary"
                data-testid="hero-cta-secondary"
                onClick={handleCtaClick('hero_secondary')}
              >
                Watch the 53-second tour
              </a>
            </div>
            {!user && (
              <p className="hero-signin-link">
                Already have an account?{' '}
                <button
                  type="button"
                  className="hero-signin-btn"
                  onClick={handleSignInCta('hero_sign_in')}
                >
                  Sign in
                </button>
              </p>
            )}
            <p className="hero-trust">
              Start free. No credit card required. No plugins, no camera subscription.
            </p>
            {!user && (
              <p className="hero-legal-notice">
                By signing up, you agree to our{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
              </p>
            )}

            <div className="hero-proof" data-testid="hero-proof">
              <div className="hero-proof-frame">
                {motionPaceHome ? (
                  /* Flag ON: triple 3-up comparison (constant / MotionTrim / MotionPace).
                     TripleHero manages its own video refs and autoplay state so an
                     off-screen refusal cannot flip the hero's showProofControls. */
                  <motionPaceHome.TripleHero
                    reduceMotion={reduceMotion}
                    showProofControls={showProofControls}
                  />
                ) : (
                  /* Flag OFF: original 2-up grid, unchanged. */
                  <div className="hero-proof-grid">
                    <div className="hero-proof-cell">
                      <span className="hero-proof-tag hero-proof-tag-before">
                        Straight off the camera
                      </span>
                      {/* Plain <video>, not TrackedVideo: these autoplay on every
                          landing view, so a video_start here would swamp the tour
                          video's engagement signal. */}
                      <video
                        ref={proofVideoRef}
                        src="/landing-proof-before.mp4"
                        poster="/landing-proof-before-poster.jpg"
                        autoPlay={!reduceMotion}
                        muted
                        loop={!reduceMotion}
                        playsInline
                        controls={showProofControls}
                        preload="metadata"
                        aria-label="One day of a deck build straight off the camera, 29 seconds"
                        data-testid="hero-proof-before-video"
                      />
                      {/* The native control bar renders its own "0:00 / 0:29" in
                          this same corner, so while it is up the badge would both
                          duplicate the duration and sit over the scrubber. */}
                      {!showProofControls && <span className="hero-proof-dur">0:29</span>}
                    </div>
                    <div className="hero-proof-cell">
                      <span className="hero-proof-tag hero-proof-tag-after">After Tweedia</span>
                      <video
                        ref={proofVideoRef}
                        src="/landing-proof-after.mp4"
                        poster="/landing-proof-after-poster.jpg"
                        autoPlay={!reduceMotion}
                        muted
                        loop={!reduceMotion}
                        playsInline
                        controls={showProofControls}
                        preload="metadata"
                        aria-label="The same day after Tweedia, 12 seconds"
                        data-testid="hero-proof-after-video"
                      />
                      {!showProofControls && <span className="hero-proof-dur">0:12</span>}
                    </div>
                  </div>
                )}
                {motionPaceHome ? (
                  <motionPaceHome.TripleHeroCaption />
                ) : (
                  <p className="hero-proof-caption">
                    One day on a deck build — <b>0:29</b> of footage down to the <b>0:12</b> where
                    something actually happens.
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>

        {motionPaceHome && (
          <motionPaceHome.MotionPaceSection
            sectionRef={setSectionRef(motionPaceHome.SECTION_ID)}
            onLearnMoreClick={handleCtaClick(motionPaceHome.LEARN_MORE_CTA_ID)}
          />
        )}

        <section
          className="ingest-section"
          aria-labelledby="ingest-heading"
          ref={setSectionRef('ingest')}
          data-section-id="ingest"
        >
          <h2 id="ingest-heading" className="visually-hidden">What you can bring</h2>
          <div className="ingest-grid">
            {/* The audience badge sits on its own line ABOVE the heading in
                both doors. Inline, one of the two always wrapped and the other
                did not — which line the pill landed on changed with the
                viewport, so the pair never looked like a pair. The badge stays
                SECOND in the DOM and is lifted visually by `order: -1`, so the
                heading's accessible name still starts with the door's own name
                ("Photo sequences") rather than the eyebrow. */}
            <div className="ingest-door" data-testid="ingest-door-photos">
              <h3>
                <span>Photo sequences</span>
                <em>Interval shooters</em>
              </h3>
              <p>
                Drop a card full of stills — hundreds or thousands at a time. Tweedia reads the
                capture date off each one, files it under the right day, and compiles the whole
                thing into video for you.{' '}
                <span className="ingest-src">No renaming, no folder wrangling.</span>
              </p>
            </div>
            <div className="ingest-door" data-testid="ingest-door-video">
              <h3>
                <span>Long video</span>
                <em>Set-and-forget cams</em>
              </h3>
              <p>
                Upload the long clips a GoPro, phone, or jobsite camera produces and skip the
                compile step entirely.{' '}
                <span className="ingest-src">Tweedia analyzes the footage you already have</span>{' '}
                and goes straight to cutting it down.
              </p>
            </div>
          </div>
          <p className="ingest-foot">
            Works with the camera you already own — GoPro, iPhone and Android, DSLR and mirrorless,
            jobsite cameras, Raspberry Pi rigs. Nothing to install.
          </p>
        </section>

        <section
          className="funnel-section"
          aria-labelledby="funnel-heading"
          ref={setSectionRef('funnel')}
          data-section-id="funnel"
        >
          <p className="section-kicker">How it works</p>
          <h2 id="funnel-heading">From camera to final cut in three steps</h2>
          <p className="section-lede">No timeline scrubbing, no plugin stack, no render farm.</p>
          <ol className="funnel-steps">
            <li className="funnel-step">
              <div className="funnel-step-header">
                <div className="funnel-step-number">1</div>
                <h3>Bring what you shot</h3>
              </div>
              <p>
                Upload photos or video straight off the card. Every frame lands on the day it was
                captured, so a long shoot arrives organized instead of as one pile.
              </p>
            </li>
            <li className="funnel-step">
              <div className="funnel-step-header">
                <div className="funnel-step-number">2</div>
                <h3>Tune the edit</h3>
              </div>
              <p>
                Switch each step on or off and watch the timeline respond as you drag. Tuning and
                previewing are free — credits are only spent when you render.
              </p>
            </li>
            <li className="funnel-step">
              <div className="funnel-step-header">
                <div className="funnel-step-number">3</div>
                <h3>Render and share</h3>
              </div>
              <p>
                Tweedia builds the finished video in the cloud. Download it and post it anywhere —
                no desktop editor involved.
              </p>
            </li>
          </ol>
          <div className="funnel-cta">
            {user ? (
              <Link
                to="/studio"
                className="cta-button cta-button-primary"
                data-testid="funnel-cta"
                onClick={handleCtaClick('funnel')}
              >
                Open the Studio
              </Link>
            ) : (
              <button
                type="button"
                className="cta-button cta-button-primary"
                onClick={handleSignInCta('funnel')}
                data-testid="funnel-cta"
              >
                Create Your First Video
              </button>
            )}
          </div>
        </section>

        <section
          className="features-section"
          aria-labelledby="features-heading"
          ref={setSectionRef('features')}
          data-section-id="features"
        >
          <p className="section-kicker">What it actually does</p>
          <h2 id="features-heading">Three problems that make long shoots unwatchable</h2>
          <p className="section-lede">
            Tweedia fixes each one, shows you its work, and lets you overrule it. Every screen
            below is the real product, measuring real footage.
          </p>

          <div className="feature-row" data-testid="feature-row-trim">
            <div className="feature-copy">
              <h3>Cut the hours where nothing happens</h3>
              <p className="feature-promise">
                A long shoot is mostly stillness — hours where the camera ran and nothing moved.
                Automatic stillness detection scores every frame for movement, keeps the stretches
                where something changes, and discards the rest.
              </p>
              <p className="feature-detail">
                Drag one slider and the timeline redraws as you go — kept in teal, discarded in
                grey. Set it loose to keep only the big moments, or ease it back to hold the quiet
                detail work.
              </p>
              <p className="feature-label">
                <span className="feature-live" /> Updates instantly, before you spend a credit
              </p>
            </div>
            <div className="feature-demo">
              <div className="feature-panel">
                {motionPaceHome ? (
                  // No proofVideoRef here on purpose: this pair is below the
                  // fold and manages its own playback, so an autoplay refusal
                  // down here cannot add controls to the hero.
                  <motionPaceHome.TrimProofPair reduceMotion={reduceMotion} />
                ) : (
                  <div className="feature-panel-media">
                    <img
                      src="/landing-trim-card.png"
                      alt="Tweedia's motion timeline for a single day, with the kept stretches highlighted and the discarded ones greyed out"
                      width="1440"
                      height="686"
                      loading="lazy"
                    />
                  </div>
                )}
                {motionPaceHome ? (
                  <motionPaceHome.TrimProofCaption />
                ) : (
                  <p className="feature-panel-foot">
                    The motion track for one day on site. Teal is kept, the gaps are discarded —{' '}
                    <b>0:29</b> of footage down to <b>0:12</b>.
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="feature-row feature-row-flip" data-testid="feature-row-flicker">
            <div className="feature-copy">
              <h3>Steady the flicker</h3>
              <p className="feature-promise">
                Cameras left outdoors re-meter every shot. Played back at speed, those tiny
                exposure shifts turn into a strobe.
              </p>
              <p className="feature-detail">
                Tweedia measures the brightness swing across your whole shoot and smooths it, while
                leaving the real changes — a sunset, a cloud crossing — alone. Pick a strength and
                it tells you what it found rather than asking you to guess.
              </p>
              <p className="feature-label">
                <span className="feature-live" /> Measured from your footage, not a stock illustration
              </p>
            </div>
            <div className="feature-demo">
              <div className="feature-panel">
                {/* The export carries ~12px of white page background along its
                    bottom edge, which painted a bright sliver across the dark
                    card. Cropped in CSS rather than by re-cutting the file: the
                    PNG is a real product screenshot and does not get
                    regenerated for a styling fix. */}
                <div className="feature-panel-media feature-panel-media--trim-bottom">
                  <img
                    src="/landing-flicker-graph.png"
                    alt="Flicker measured across a whole shoot, with the estimated after-fix line overlaid"
                    width="1440"
                    height="649"
                    loading="lazy"
                  />
                </div>
                <p className="feature-panel-foot">
                  Amber is the flicker Tweedia measured in this shoot — <b>3.17%</b> frame to
                  frame, where <b>1%</b> is already visible. Teal is its estimate of what is left
                  after the fix.
                </p>
              </div>
            </div>
          </div>

          <div className="feature-row" data-testid="feature-row-days">
            <div className="feature-copy">
              <h3>Organized by capture day</h3>
              <p className="feature-promise">
                Drop months of frames in at once. Every photo lands on the day it was actually
                shot — read from the file, not the filename.
              </p>
              <p className="feature-detail">
                A 90-day build stays reviewable one day at a time instead of becoming an
                undifferentiated pile. Open a day to see just that day's frames. The days the
                camera never ran simply are not there.
              </p>
              <p className="feature-label">
                <span className="feature-live" /> Sorted on upload, automatically
              </p>
            </div>
            <div className="feature-demo">
              <div className="feature-panel">
                <div className="feature-panel-media">
                  <img
                    src="/landing-day-grid.webp"
                    alt="Uploaded photos grouped into dated capture days, each row showing that day's frames and count"
                    width="1188"
                    height="602"
                    loading="lazy"
                  />
                </div>
                <p className="feature-panel-foot">
                  This project: <b>839 photos</b> across <b>14 capture days</b>, filed without
                  touching a single filename. Each day shows its own frames and count.
                </p>
              </div>
            </div>
          </div>

          <div className="feature-more">
            <div className="feature-more-item">
              <h4>Bring any camera</h4>
              <p>
                GoPro, phone, DSLR, jobsite cam, or a Raspberry Pi on a fence post. Photos or
                video, both work — nothing to install and no camera subscription to buy.
              </p>
            </div>
            <div className="feature-more-item">
              <h4>Compile without an editor</h4>
              <p>
                Turn an image sequence into a finished timelapse in the cloud. Choose the frame
                rate; Tweedia builds the file and hands it back.
              </p>
            </div>
            <div className="feature-more-item">
              <h4>Storage that keeps the shoot alive</h4>
              <p>
                Pooled cloud storage across every project, so a six-month build is not stranded on
                one SD card. 2 GB free, 50 GB on Solo. On Free, raw photos are cleared 30 days
                after a project's last activity.
              </p>
            </div>
          </div>
        </section>

        <section
          className="brand-section"
          aria-labelledby="brand-heading"
          ref={setSectionRef('brand')}
          data-section-id="brand"
        >
          <p className="section-kicker">Make it yours</p>
          <h2 id="brand-heading">Your brand on every video</h2>
          <p className="section-lede">
            Free renders carry a small Tweedia mark. Solo and Pro replace it with your own
            logo — or switch marking off entirely.
          </p>
          <div className="feature-row" data-testid="feature-row-watermark">
            <div className="feature-copy">
              <h3>Upload a logo once, wear it everywhere</h3>
              <p className="feature-promise">
                Drop in your logo — PNG, JPEG, WebP or GIF — and every video you render
                carries it. Pick the corner, size it against the frame, and set how strongly
                it shows — the live preview redraws on a real frame of your own footage as
                you drag.
              </p>
              <p className="feature-detail">
                Your choices become the default for every new project, so the next render
                already looks like you. Delivering to a client instead? Switch the mark off
                for a clean cut.
              </p>
              <p className="feature-label">
                <span className="feature-live" /> Included with Solo and Pro — set once, on
                every render
              </p>
            </div>
            <div className="feature-demo">
              <div className="feature-panel">
                <div className="feature-panel-media">
                  <img
                    src="/landing-watermark-card.webp"
                    alt="Tweedia's watermark settings card: a custom logo overlaid on a real frame of the user's footage, with logo, corner, size, and opacity controls"
                    width="1440"
                    height="450"
                    loading="lazy"
                  />
                </div>
                <p className="feature-panel-foot">
                  The real settings card: a builder's logo over their own deck build —{' '}
                  <b>bottom right</b>, <b>18%</b> of frame width, full opacity.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section
          id="tour"
          className="tour-section"
          aria-labelledby="tour-heading"
          ref={setSectionRef('tour')}
          data-section-id="tour"
        >
          <p className="section-kicker">See it end to end</p>
          <h2 id="tour-heading" className="tour-section-title">The whole thing, in 53 seconds</h2>
          <p className="section-lede">
            The problem, the fix, and what it looks like inside the studio.
          </p>
          <div className="tour-video-wrap">
            <TrackedVideo
              videoId="tour_60s"
              controls
              controlsList="nodownload noplaybackrate noremoteplayback"
              disableRemotePlayback
              playsInline
              preload="metadata"
              poster="/tweedia_tour_poster.jpg"
              aria-label="Tweedia 53-second product tour"
            >
              <source src="/tweedia_tour_54s.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </TrackedVideo>
          </div>
        </section>

        <section
          id="pricing"
          className="pricing-section"
          aria-labelledby="pricing-heading"
          ref={setSectionRef('pricing')}
          data-section-id="pricing"
        >
          <p className="section-kicker">Plans &amp; pricing</p>
          <h2 id="pricing-heading">Start free. Upgrade when the shoot gets long.</h2>
          <p className="section-lede">
            Pooled storage across every project, monthly render credits, and watermark-free
            exports. Founding rates stay locked while you stay subscribed.
          </p>

          <div className="pricing-plans" data-testid="landing-plans-summary">
            {PRICING_TIERS.map((tier) => (
              <div
                key={tier.id}
                className={`pricing-plan${tier.featured ? ' pricing-plan-featured' : ''}`}
                data-testid={`landing-plan-${tier.id}`}
              >
                {tier.founding && <span className="pricing-plan-flag">Founding</span>}
                <h3 className="pricing-plan-name">{tier.name}</h3>
                <p className="pricing-plan-price">
                  {tier.priceLabel}
                  <span className="pricing-plan-suffix">{tier.priceSuffix}</span>
                </p>
                <ul className="pricing-plan-list">
                  {planBullets(tier).map((bullet) => (
                    <li key={bullet}>{bullet}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <Link
            to="/pricing"
            className="pricing-see-all"
            data-testid="landing-pricing-see-all"
            onClick={handleCtaClick('pricing_see_all')}
          >
            See full pricing &amp; FAQ →
          </Link>

          <p className="pricing-footnote">
            Credits pay for rendering — longer footage and higher source resolutions use more.
            Top-up packs are available any time on a paid plan.
          </p>
        </section>

        <section
          id="faq"
          className="faq-section"
          aria-labelledby="faq-heading"
          ref={setSectionRef('faq')}
          data-section-id="faq"
        >
          <p className="section-kicker">Before you sign up</p>
          <h2 id="faq-heading">The questions everyone asks first</h2>
          <div className="faq-list">
            {FAQS.map(({ q, a }) => (
              <div className="faq-item" key={q}>
                <h3>{q}</h3>
                <p>{a}</p>
              </div>
            ))}
          </div>
        </section>

        <section
          className="final-cta-section"
          ref={setSectionRef('final_cta')}
          data-section-id="final_cta"
        >
          <h2>Ready to edit smarter?</h2>
          <p className="subtitle">
            Sign up free, drop in your first batch of photos or footage, and watch the stillness
            disappear.
          </p>
          <div className="hero-cta-row">
            {user ? (
              <Link
                to="/studio"
                className="try-now-link"
                data-testid="final-cta-primary"
                onClick={handleCtaClick('final_primary')}
              >
                <StartEditingButton />
              </Link>
            ) : (
              <button
                type="button"
                className="cta-button cta-button-primary"
                onClick={handleSignInCta('final_primary')}
                data-testid="final-cta-primary"
              >
                Sign Up Free
              </button>
            )}
          </div>
          <p className="hero-trust">No credit card required.</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

export default LandingPage;
